// START IMPORTS
// Context imports
import { AppConfig } from '@ltvco/refresh-lib/ctx';
// Core lib v1 imports
import {
  EmailVerificationBlocker,
  EmailVerificationErrorModal,
  EmailVerificationInvalidModal,
  EmailVerificationSuccessModal,
  useAccount,
} from '@ltvco/refresh-lib/v1';
// Vendor imports
import { useFeatureIsOn } from '@ltvco/refresh-lib/vendors';
import { useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useQueryParam, StringParam } from 'use-query-params';
// END IMPORTS

// START CONSTANTS
const VERIFICATION_EVENT_ACTION = 'view';
const VERIFICATION_EVENT_LABEL = 'email verification result modal';
// END CONSTANTS

export function EmailVerification({ children }: { children: JSX.Element }) {
  // START HOOKS_AND_QUERIES
  const location = useLocation();
  const { data: account } = useAccount(true);
  const [statusParam, setStatusParam] = useQueryParam('status', StringParam);
  const { trackEvent } = useContext(AppConfig);
  const isInEmailBlockerVariation = useFeatureIsOn('mb5k-81');
  // END HOOKS_AND_QUERIES

  // START STATE_MANAGEMENT
  const [showEmailExpiredModal, setShowEmailExpiredModal] = useState(false);
  const [showEmailSuccessModal, setShowEmailSuccessModal] = useState(false);
  const [showEmailBlockerPage, setShowEmailBlockerPage] = useState(false);
  const [showEmailInvalidModal, setShowEmailInvalidModal] = useState(false);
  // END STATE_MANAGEMENT

  // START DERIVED_DATA_AND_UTILS
  const isOnboardingPage = location.pathname.includes('/onboarding');
  // END DERIVED_DATA_AND_UTILS

  // START EFFECTS
  useEffect(() => {
    if (!account) {
      return;
    }
    if (isOnboardingPage) {
      setShowEmailBlockerPage(false);
    }

    const isEmailVerified = account?.account.user_info?.verified_email;

    if (isEmailVerified && statusParam === 'success') {
      setStatusParam(undefined, 'replaceIn');
      setShowEmailSuccessModal(true);
      trackEvent(
        'email verification success',
        VERIFICATION_EVENT_ACTION,
        VERIFICATION_EVENT_LABEL
      );
    } else if (!isEmailVerified && statusParam === 'expired') {
      setStatusParam(undefined, 'replaceIn');
      setShowEmailExpiredModal(true);
      trackEvent(
        'email verification expired',
        VERIFICATION_EVENT_ACTION,
        VERIFICATION_EVENT_LABEL
      );
    } else if (!isEmailVerified && statusParam === 'invalid') {
      setStatusParam(undefined, 'replaceIn');
      setShowEmailInvalidModal(true);
      trackEvent(
        'email verification invalid',
        VERIFICATION_EVENT_ACTION,
        VERIFICATION_EVENT_LABEL
      );
    } else if (
      !isEmailVerified &&
      isInEmailBlockerVariation &&
      !showEmailExpiredModal &&
      !showEmailInvalidModal &&
      !isOnboardingPage
    ) {
      setShowEmailBlockerPage(true);
      trackEvent(
        'view blocker',
        VERIFICATION_EVENT_ACTION,
        'email verification blocker'
      );
    }
  }, [
    account,
    statusParam,
    setStatusParam,
    isInEmailBlockerVariation,
    isOnboardingPage,
  ]);
  // END EFFECTS

  // START RENDER
  return (
    <div>
      {isInEmailBlockerVariation && (
        <EmailVerificationBlocker open={showEmailBlockerPage} />
      )}
      <EmailVerificationErrorModal
        open={showEmailExpiredModal}
        onClose={() => setShowEmailExpiredModal(false)}
      />
      <EmailVerificationSuccessModal
        open={showEmailSuccessModal}
        onClose={() => setShowEmailSuccessModal(false)}
      />
      <EmailVerificationInvalidModal
        open={showEmailInvalidModal}
        onClose={() => setShowEmailInvalidModal(false)}
      />
      {children}
    </div>
  );
  // END RENDER
}
